/* eslint-disable @typescript-eslint/naming-convention */
import SvgIcon from '@home/components/SvgIcon';
import Bubble from '@home/components/TeaComponents/Bubble';
import BubbleCommon, { commonBrandHealthGradeTips, commonBrandHealthTips, commonSentimentTips, engagementTips, mentionsTips } from '@op/components/BubbleCommon';
import CardCommon from '@op/components/CardCommon';
import BrandHealthCharts from '@op/components/Dashboard/BrandHealthCharts';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import RankCard from '@op/pages/Overview/KeyMetrics/MentionsSentiment/components/RankCard';
import SmallChart from '@op/pages/Overview/KeyMetrics/MentionsSentiment/components/SmallChart';
import { isOneDay } from '@op/utils';
import { calculateBrandHealthRate } from '@op/utils/text';
import { formatTimeToShow, getFilterDateQOQText } from '@op/utils/time';
import { brandHealthFormat, isNilOrEmpty, percentageFormat, reviewsFormat, sentimentFormat } from '@op/utils/value';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { Col, Row } from 'tea-component';
import { useIntl } from 'umi';
import Abnormal from '@home/components/Abnormal';
import { useKeyMetricsMentionsList, useKeyMetricsSentimentList } from './hooks';
import './index.scss';
import { JumpType } from '@home/utils/utils';

type ChartTipsElementProps = {
  title: string; // 浮窗标题
  value: number; // 浮窗值
  positiveValue: number; // 正面值
  positivePercentValue: number; // 正面占比
  neutralValue: number; // 中立值
  neutralPercentValue: number; // 中立占比
  negativeValue: number; // 负面值
  negativePercentValue: number; // 负面占比
};

export default function KPIsCard(props: any) {
  const { filter, filterCond, gameData, wholeGameInfo, showRank, competitorGames } = props || {};
  const { formatMessage } = useIntl();

  const params = useMemo(
    () => {
      if (!filterCond) return {};
      return {
        ...filter,
        tab_type: 'total',
        games: competitorGames,
        social_media_by_account: filterCond?.social_media_by_account || undefined,
        social_media_by_keyword: undefined
      };
    },
    [filterCond, filter, competitorGames],
  );

  const sentimentParams = useMemo(
    () => {
      if (!filterCond) return {};
      return {
        ...filter,
        ...wholeGameInfo,
        statistics_field: 'mentions',
        social_media_by_account: filterCond?.social_media_by_account || undefined,
        social_media_by_keyword: undefined
      };
    },
    [filter, filterCond, wholeGameInfo],
  );

  const filterParams = useMemo(() => {
    if (!filter) return {};

    return {
      date_type: filter?.date_type,
      start_time: filter?.start_time,
      end_time: filter?.end_time,
    };
  }, [filter]);

  const { data, loading } = useKeyMetricsMentionsList({
    filter: params,
  });

  const { data: sentimentData } =
    useKeyMetricsSentimentList({
      filter: sentimentParams,
    });

  const timeShow = useCallback(
    (timeRange: [string, string]) => {
      const [start_time, end_time] = timeRange;

      if (filter.date_type === 'hourly') {
        return [
          `${moment(start_time).format('YYYY-MM-DD HH:mm')}~${moment(
            end_time,
          ).format('YYYY-MM-DD HH:mm')}`,
        ];
      }
      if (isOneDay(filter)) {
        return [moment(start_time).format('YYYY-MM-DD')];
      }
      return [
        `${moment(start_time).format('YYYY-MM-DD')}~${moment(end_time).format(
          'YYYY-MM-DD',
        )}`,
      ];
    },
    [filter],
  );

  const {
    avg_sentiment,
    avg_sentiment_old,
    avg_sentiment_ratio,
    brand_health,
    brand_health_old,
    brand_health_ratio,
    mentions,
    mentions_old,
    mentions_ratio,
    negative_mentions,
    neutral_mentions,
    positive_mentions,
    engagement,
    engagement_old,
    engagement_ratio,
    negative_engagement,
    neutral_engagement,
    positive_engagement,
    rankData = {},
    newTime,
    oldTime,
  } = useMemo(() => ({
    // Mentions 数据
    mentions: data?.mentions || 0,
    positive_mentions: data?.positive_mentions || 0,
    negative_mentions: data?.negative_mentions || 0,
    neutral_mentions: data?.neutral_mentions || 0,
    mentions_old: data?.mentions_old || 0,
    mentions_ratio: data?.mentions_ratio || 0,
    // Engagement 数据
    engagement: data?.engagement || 0,
    positive_engagement: data?.positive_engagement || 0,
    negative_engagement: data?.negative_engagement || 0,
    neutral_engagement: data?.neutral_engagement || 0,
    engagement_old: data?.engagement_old || 0,
    engagement_ratio: data?.engagement_ratio || 0,
    // Brand Health 数据
    brand_health: data?.brand_health,
    brand_health_old: data?.brand_health_old || null,
    brand_health_ratio: data?.brand_health_ratio || 0,
    // Sentiment 数据
    avg_sentiment: data?.avg_sentiment || 0,
    avg_sentiment_old: data?.avg_sentiment_old || 0,
    avg_sentiment_ratio: data?.avg_sentiment_ratio || 0,

    rankData: data?.rankData || {},
    newTime: data?.newTime || [],
    oldTime: data?.oldTime || [],
  }), [data]);

  // Mentions 占比计算
  const positivePercent = (positive_mentions / mentions) * 100;
  const negativePercent = (negative_mentions / mentions) * 100;
  const neutralPercent = (neutral_mentions / mentions) * 100;
  // Engagement 占比计算
  const engagementPositivePercent = (positive_engagement / engagement) * 100;
  const engagementNegativePercent = (negative_engagement / engagement) * 100;
  const engagementNeutralPercent = (neutral_engagement / engagement) * 100;

  const formatRankData = useMemo(() => {
    const {
      mentions: mentionsRank = [],
      engagement: engagementRank = [],
      brand_health: brandHealthRank = [],
      avg_sentiment: avgSentimentRank = [],
    } = rankData;

    const replaceGameKeyWithGameData = (
      rankArray: { game: string; value: number; }[],
    ) =>
      rankArray.map((rankItem) => ({
        ...rankItem,
        ...(gameData?.[rankItem.game] || {}),
      }));

    return {
      mentions: replaceGameKeyWithGameData(mentionsRank),
      engagement: replaceGameKeyWithGameData(engagementRank),
      brand_health: replaceGameKeyWithGameData(brandHealthRank),
      avg_sentiment: replaceGameKeyWithGameData(avgSentimentRank),
    };
  }, [rankData, gameData]);

  const brandHealth = calculateBrandHealthRate(brand_health);
  const { text: beforeText } = getFilterDateQOQText(filter);

  // 横条占比图浮窗
  function ChartTipsElement({
    value,
    title,
    positiveValue,
    positivePercentValue,
    neutralValue,
    neutralPercentValue,
    negativeValue,
    negativePercentValue,

  }: ChartTipsElementProps) {
    return (
      <div className="chart-common-formatter">
        <div className="chart-common-formatter-header">
          <div className="chart-common-formatter-header-left">
            {formatTimeToShow(filter)}
          </div>
          <div className="chart-common-formatter-header-right"></div>
        </div>
        <table className="undefined">
          <tbody>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                {formatMessage({ id: title })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(value)}
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#69D19C' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Positive' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(positiveValue)} (
                {percentageFormat(positivePercentValue)})
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#7E92A1' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Neutral' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(neutralValue)} (
                {percentageFormat(neutralPercentValue)})
              </td>
            </tr>
            <tr>
              <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                <span className="chart-common-formatter-marker">
                  <span
                    className="chart-common-marker-dot"
                    style={{ backgroundColor: '#F17C86' }}
                  ></span>
                </span>
                {formatMessage({ id: 'Negative' })}
              </td>
              <td
                className="bold"
                style={{ textAlign: 'right', fontWeight: 'bold' }}
              >
                {reviewsFormat(negativeValue)} (
                {percentageFormat(negativePercentValue)})
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  
  return (
    <Abnormal
      isLoading={loading}
      // isError={usertag.errorMsg || retention.errorMsg}
      classNames={'dbh-kpi-card-abnormal'}
      component={
        <CardCommon contentCard className="metrics-list-wrapper dbh-studio-kpi-card-wrapper">
          <Row className="dbo-rank-row">
            <Col span={6}>
              <RankCard
                title={formatMessage({ id: 'Mentions' })}
                rankData={formatRankData.mentions}
                currentGame={wholeGameInfo?.edition_unified_id}
                valueFormat={reviewsFormat}
                tips={mentionsTips}
                showRank={showRank}
                canNavigateToGame
                jumpType={JumpType.Window}
                filter={filterParams}
              >
                <div className="dbo-compare">
                  <Bubble
                    className="dbo-noborer-wrap"
                    placement="right"
                    closeOnScroll
                    content={
                      <ChartTipsElement
                        title="Mentions"
                        value={mentions}
                        positiveValue={positive_mentions}
                        positivePercentValue={positivePercent}
                        neutralValue={neutral_mentions}
                        neutralPercentValue={neutralPercent}
                        negativeValue={negative_mentions}
                        negativePercentValue={negativePercent}
                      />
                    }
                    dark
                  >
                    <div className="dbo-compare-bubble">
                      <div className="dbo-compare-num">
                        {reviewsFormat(mentions)}
                      </div>
                      <div className="dbo-compare-bar">
                        <div
                          className="dbo-compare-bar-item"
                          style={{
                            width: percentageFormat(positivePercent),
                            backgroundColor: '#69D19C',
                          }}
                        ></div>
                        <div
                          className="dbo-compare-bar-item"
                          style={{
                            width: percentageFormat(neutralPercent),
                            backgroundColor: '#6E7794',
                          }}
                        ></div>
                        <div
                          className="dbo-compare-bar-item"
                          style={{
                            width: percentageFormat(negativePercent),
                            backgroundColor: '#F17C86',
                          }}
                        ></div>
                      </div>
                    </div>
                  </Bubble>
                  <div className="dbo-compare-flex">
                    <RingRatioBubble
                      otherInfo={
                        <div className="item-title">
                          {formatMessage({ id: 'Mentions' })}{' '}
                          {formatMessage({ id: beforeText })}
                        </div>
                      }
                      newTime={timeShow(newTime)}
                      oldTime={timeShow(oldTime)}
                      oldValue={brandHealthFormat(mentions_old)}
                      newValue={brandHealthFormat(mentions)}
                      placement="top-start"
                    >
                      <>
                        <span className="context-text">
                          {formatMessage({ id: beforeText })}
                        </span>
                        <PercentageItem
                          percentage={mentions_ratio}
                        ></PercentageItem>
                      </>
                    </RingRatioBubble>
                  </div>
                </div>
              </RankCard>
            </Col>
            <Col span={6}>
              <RankCard
                title={formatMessage({ id: 'Engagement' })}
                rankData={formatRankData.engagement}
                currentGame={wholeGameInfo?.edition_unified_id}
                valueFormat={reviewsFormat}
                tips={engagementTips}
                showRank={showRank}
                canNavigateToGame
                jumpType={JumpType.Window}
                filter={filterParams}
              >
                <div className="dbo-compare">
                  <Bubble
                    className="dbo-noborer-wrap"
                    placement="right"
                    closeOnScroll
                    content={
                      <ChartTipsElement
                        title="Engagement"
                        value={engagement}
                        positiveValue={positive_engagement}
                        positivePercentValue={engagementPositivePercent}
                        neutralValue={neutral_engagement}
                        neutralPercentValue={engagementNeutralPercent}
                        negativeValue={negative_engagement}
                        negativePercentValue={engagementNegativePercent}
                      />
                    }
                    dark
                  >
                    <div className="dbo-compare-bubble">
                      <div className="dbo-compare-num">
                        {reviewsFormat(engagement)}
                      </div>
                      <div className="dbo-compare-bar" style={{ top: '76px', right: '20px' }}>
                        <div
                          className="dbo-compare-bar-item"
                          style={{
                            width: percentageFormat(engagementPositivePercent),
                            backgroundColor: '#69D19C',
                          }}
                        ></div>
                        <div
                          className="dbo-compare-bar-item"
                          style={{
                            width: percentageFormat(engagementNeutralPercent),
                            backgroundColor: '#6E7794',
                          }}
                        ></div>
                        <div
                          className="dbo-compare-bar-item"
                          style={{
                            width: percentageFormat(engagementNegativePercent),
                            backgroundColor: '#F17C86',
                          }}
                        ></div>
                      </div>
                    </div>
                  </Bubble>
                  <div className="dbo-compare-flex">
                    <RingRatioBubble
                      otherInfo={
                        <div className="item-title">
                          {formatMessage({ id: 'Engagement' })}{' '}
                          {formatMessage({ id: beforeText })}
                        </div>
                      }
                      newTime={timeShow(newTime)}
                      oldTime={timeShow(oldTime)}
                      oldValue={brandHealthFormat(engagement_old)}
                      newValue={brandHealthFormat(engagement)}
                      placement="top-start"
                    >
                      <>
                        <span className="context-text">
                          {formatMessage({ id: beforeText })}
                        </span>
                        <PercentageItem
                          percentage={engagement_ratio}
                        ></PercentageItem>
                      </>
                    </RingRatioBubble>
                  </div>
                </div>
              </RankCard>
            </Col>
            <Col span={6}>
              <RankCard
                title={formatMessage({ id: 'Brand Health' })}
                rankData={formatRankData.brand_health}
                currentGame={wholeGameInfo?.edition_unified_id}
                valueFormat={brandHealthFormat}
                showRank={showRank}
                tips={commonBrandHealthTips}
                canNavigateToGame
                jumpType={JumpType.Window}
                filter={filterParams}
              >
                <div className="dbo-compare">
                  <div className="dbo-compare-num">
                    <BubbleCommon
                      dark
                      placement="top"
                      tipsMap={
                        isNilOrEmpty(brand_health)
                          ? null
                          : commonBrandHealthGradeTips(brandHealth.text)
                      }
                      showIcon={false}
                    >
                      <span className="dbo-compare-num-healthy-text">
                        {formatMessage({ id: brandHealth.text })}
                      </span>
                    </BubbleCommon>
                  </div>
                  <div className="dbo-compare-flex">
                    <RingRatioBubble
                      otherInfo={
                        <div className="item-title">
                          {formatMessage({ id: 'Brand Health' })}{' '}
                          {formatMessage({ id: beforeText })}
                        </div>
                      }
                      newTime={timeShow(newTime)}
                      oldTime={timeShow(oldTime)}
                      oldValue={brandHealthFormat(brand_health_old)}
                      newValue={brandHealthFormat(brand_health)}
                      placement="top-start"
                    >
                      <>
                        <span className="context-text">
                          {formatMessage({ id: beforeText })}
                        </span>
                        <span
                          className={
                            brand_health_ratio > 0
                              ? 'context-success-text'
                              : 'context-red-text'
                          }
                        >
                          {percentageFormat(brand_health_ratio)}
                        </span>
                        <SvgIcon
                          type={
                            brand_health_ratio > 0 ? 'green-up-new' : 'red-down-new'
                          }
                          className="dbo-compare-icon"
                        />
                      </>
                    </RingRatioBubble>
                  </div>
                  {!isNilOrEmpty(brand_health) && (
                    <div className="dbo-compare-chart">
                      <BrandHealthCharts
                        brandHealth={Number(brandHealthFormat(brand_health))}
                      />
                      <div className="dbo-compare-chart-num">-100~100</div>
                    </div>
                  )}
                </div>
              </RankCard>
            </Col>
            <Col span={6}>
              <RankCard
                title={formatMessage({ id: 'Sentiment' })}
                rankData={formatRankData.avg_sentiment}
                currentGame={wholeGameInfo?.edition_unified_id}
                valueFormat={sentimentFormat}
                showRank={showRank}
                tips={commonSentimentTips}
                canNavigateToGame
                jumpType={JumpType.Window}
                filter={filterParams}
              >
                <div className="dbo-compare">
                  <div className="dbo-compare-num">
                    {sentimentFormat(avg_sentiment)}
                    <span className="dbo-compare-num-level">
                      {formatMessage({ id: 'Up to 5' })}
                    </span>
                  </div>
                  <div className="dbo-compare-flex">
                    <RingRatioBubble
                      otherInfo={
                        <div className="item-title">
                          {formatMessage({ id: 'Sentiment' })}{' '}
                          {formatMessage({ id: beforeText })}
                        </div>
                      }
                      newTime={timeShow(newTime)}
                      oldTime={timeShow(oldTime)}
                      oldValue={sentimentFormat(avg_sentiment_old)}
                      newValue={sentimentFormat(avg_sentiment)}
                      placement="top-start"
                    >
                      <>
                        <span className="context-text">
                          {formatMessage({ id: beforeText })}
                        </span>
                        <span
                          className={
                            avg_sentiment_ratio > 0
                              ? 'context-success-text'
                              : 'context-red-text'
                          }
                        >
                          {percentageFormat(avg_sentiment_ratio)}
                        </span>
                        <SvgIcon
                          type={
                            avg_sentiment_ratio > 0
                              ? 'green-up-new'
                              : 'red-down-new'
                          }
                          className="dbo-compare-icon"
                        />
                      </>
                    </RingRatioBubble>
                  </div>
                  <div className="dbo-compare-chart dbo-compare-small-chart">
                    <SmallChart
                      text={''}
                      statistics={{
                        x: sentimentData.xAxisData,
                        y: sentimentData.yAxisData,
                      }}
                      topicName={formatMessage({ id: 'Sentiment' })}
                      formatValueFunc={(value: number) => sentimentFormat(value)}
                      dateType={filter?.date_type}
                    />
                  </div>
                </div>
              </RankCard>
            </Col>
          </Row>
        </CardCommon>
      }
    />
  );
}
